import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Copy, Download, Lock, Globe, Key, Check } from 'lucide-react';
import { formatFileSize } from '../mockData';
import { useToast } from '../hooks/use-toast';

const FileViewerDialog = ({ file, open, onClose, onDownload }) => {
  const [showTokenGenerator, setShowTokenGenerator] = useState(false);
  const [tokenExpiry, setTokenExpiry] = useState('3600');
  const [tokenMaxUses, setTokenMaxUses] = useState('5');
  const [generatedToken, setGeneratedToken] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  React.useEffect(() => {
    let active = true;
    let url = null;

    const fetchPreview = async () => {
      // For public files, use direct URL if present
      if (file.isPublic && file.url) {
        setPreviewUrl(file.url);
        return;
      }

      if (!file.contentType.startsWith('image/')) return;

      setLoadingPreview(true);
      setPreviewError(null);
      try {
        const { filesApi } = await import('../lib/api');

        // Avoid blob: URLs due to CSP. Use direct API URL for public images.
        if (file.isPublic) {
          setPreviewUrl(filesApi.getPreviewUrl(file.id));
          return;
        }

        // For private images, fetch as blob then convert to data: URL (allowed by CSP)
        const blob = await filesApi.download(file.id);
        if (!blob) throw new Error('No content received');

        if (blob.type === 'application/json') {
          const text = await blob.text();
          let errorMessage = 'Failed to load image';
          try {
            const json = JSON.parse(text);
            errorMessage = json.error || json.message || errorMessage;
          } catch (e) { }
          throw new Error(errorMessage);
        }

        const toDataUrl = (b) => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(b);
        });

        const dataUrl = await toDataUrl(blob);
        if (active) setPreviewUrl(dataUrl);
      } catch (error) {
        console.error('Failed to load preview:', error);
        if (active) setPreviewError('Failed to load image preview');
      } finally {
        if (active) setLoadingPreview(false);
      }
    };

    if (open && file) {
      fetchPreview();
    } else {
      setPreviewUrl(null);
      setLoadingPreview(false);
    }

    return () => {
      active = false;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [file, open]);

  const handleGenerateToken = async () => {
    try {
      const { filesApi } = await import('../lib/api');
      const data = await filesApi.createToken(file.id, {
        expiresIn: parseInt(tokenExpiry),
        maxUses: tokenMaxUses === 'unlimited' ? 1000 : parseInt(tokenMaxUses)
      });
      setGeneratedToken(data.token);
      toast({
        title: 'Download token generated',
        description: 'Token has been generated successfully.'
      });
    } catch (error) {
      console.error('Failed to generate token:', error);
      toast({
        title: 'Generation failed',
        description: 'Failed to generate download token.',
        variant: 'destructive'
      });
    }
  };

  const handleCopyToken = () => {
    const url = `${window.location.origin}/files/${file.id}?t=${generatedToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: 'Copied to clipboard',
      description: 'Download URL has been copied.'
    });
  };

  const renderFilePreview = () => {
    if (file.contentType.startsWith('image/')) {
      return (
        <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
          {loadingPreview ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-2"></div>
              <p className="text-gray-500 text-sm">Loading preview...</p>
            </div>
          ) : previewError ? (
            <div className="text-red-500 text-sm">{previewError}</div>
          ) : previewUrl ? (
            <img src={previewUrl} alt={file.name} className="max-w-full max-h-full object-contain" />
          ) : (
            <p className="text-gray-400">No preview available</p>
          )}
        </div>
      );
    }

    if (file.contentType === 'application/pdf') {
      return (
        <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 mb-4">PDF Preview</p>
            <p className="text-sm text-gray-500">In production, this would show a PDF viewer</p>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Document Preview</p>
          <p className="text-sm text-gray-500">In production, this would show a document viewer</p>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{file.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {renderFilePreview()}

          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-xs text-gray-500 mb-1">Size</p>
              <p className="text-sm font-medium">{formatFileSize(file.size)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Type</p>
              <p className="text-sm font-medium">{file.contentType.split('/')[1]}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Visibility</p>
              <div className="flex items-center">
                {file.isPublic ? (
                  <span className="inline-flex items-center text-sm font-medium text-green-700">
                    <Globe className="h-4 w-4 mr-1" /> Public
                  </span>
                ) : (
                  <span className="inline-flex items-center text-sm font-medium text-orange-700">
                    <Lock className="h-4 w-4 mr-1" /> Private
                  </span>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded</p>
              <p className="text-sm font-medium">{
                (() => {
                  const v = file.uploaded_at ?? file.uploadedAt;
                  if (!v) return 'Unknown';
                  const num = Number(v);
                  const d = !Number.isNaN(num)
                    ? new Date(num * (num < 10000000000 ? 1000 : 1))
                    : new Date(v);
                  return isNaN(d.getTime()) ? 'Unknown' : d.toLocaleDateString();
                })()
              }</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                onClick={onDownload}
                className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
              {!file.isPublic && (
                <Button
                  onClick={() => setShowTokenGenerator(!showTokenGenerator)}
                  variant="outline"
                  className="flex-1 border-teal-500 text-teal-700 hover:bg-teal-50"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Generate Token
                </Button>
              )}
            </div>

            {!file.isPublic && showTokenGenerator && (
              <div className="p-4 border border-gray-200 rounded-lg space-y-4">
                <h4 className="font-medium text-gray-900">Generate Download Token</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expiry">Expires In</Label>
                    <Select value={tokenExpiry} onValueChange={setTokenExpiry}>
                      <SelectTrigger id="expiry">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3600">1 hour</SelectItem>
                        <SelectItem value="21600">6 hours</SelectItem>
                        <SelectItem value="86400">24 hours</SelectItem>
                        <SelectItem value="604800">7 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxUses">Max Uses</Label>
                    <Select value={tokenMaxUses} onValueChange={setTokenMaxUses}>
                      <SelectTrigger id="maxUses">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 use</SelectItem>
                        <SelectItem value="5">5 uses</SelectItem>
                        <SelectItem value="10">10 uses</SelectItem>
                        <SelectItem value="unlimited">Unlimited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {!generatedToken ? (
                  <Button
                    onClick={handleGenerateToken}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
                  >
                    Generate Token
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Label>Download URL</Label>
                    <div className="flex gap-2">
                      <Input
                        value={`${window.location.origin}/files/${file.id}?t=${generatedToken}`}
                        readOnly
                        className="font-mono text-sm"
                      />
                      <Button
                        onClick={handleCopyToken}
                        variant="outline"
                        className="shrink-0"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FileViewerDialog;
